/*
 * Theme state: light / dark / system. The provider (ThemeProvider.tsx) applies
 * `.dark` / `.light` classes on <html>; src/styles/tokens.css does the rest.
 * Default is system (prefers-color-scheme). The choice persists to
 * localStorage under THEME_STORAGE_KEY.
 */
import { createContext, useContext } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'screenatlas.theme';

const PREFERENCES: readonly ThemePreference[] = ['light', 'dark', 'system'];

export interface ThemeContextValue {
  /** What the user chose (light / dark / system). */
  preference: ThemePreference;
  /** The theme actually applied after resolving `system`. */
  resolved: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Read the current theme choice. Throws outside a <ThemeProvider>. */
export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme must be used within <ThemeProvider>');
  return value;
}

export function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches === true
  );
}

/** Read the persisted choice; anything missing or malformed resolves to system. */
export function readStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return PREFERENCES.includes(stored as ThemePreference)
      ? (stored as ThemePreference)
      : 'system';
  } catch {
    return 'system';
  }
}

export function storePreference(preference: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // storage unavailable (private mode, quota) — the session still works
  }
}
